import { canApproveForm } from '../onboarding-action-policy.service';
import templateUrl from './il-w4.component.html';
import moment from 'moment';
import { PreviewSignatureModal } from '../preview-signature-modal/preview-signature-modal.component';
import { InfoModal } from '../info-modal/info-modal.component';
import { FormChoiceModal } from '../form-choice-modal/form-choice-modal.component';
import { OwnerPreviewSignatureModal } from '../preview-signature-modal/owner-preview-signature-modal.component';

// Illinois IL-W-4 (StateProvinceID 15, requestTypeId 2). APEX-321.
// Employee-only certificate (no employer section). Certificate-only (worksheet not collected).
// The template "exempt" field is a text field, so the client sends "X" when checked.
// Numeric allowance lines autofill "0" when left blank.
const IL_STATE_ID = 15;
// AdditionalFields (addlInputs) key recording the IL nonresident/military-spouse exempt opt-out (option 2).
const IL_EXEMPT_FIELD = 'StateW4Exempt'; // shared key across state exempt flows (IL, PA...)

export const IlW4Component = {
    templateUrl,
    controller: IlW4Controller,
    controllerAs: "vm",
    bindings: {
        resolve: "<",
        ownerView: "<",
        owner: "<",
        employee: "<",
        employeeProfile: "<",
        profile: "<",
        employeeBankInfo: "<",
        provinces: "<",
        application: "<",
        genderOptions: "<",
        userInputs: "<",
        savedInputs: "<",
        maritalStatus: "<",
        addlInputs: "<",
        ownerInfo: "<",
        stateTaxInfo: "<",
        showSignaturePopup: "<",
        close: "&",
        dismiss: "&"
    }
};

function IlW4Controller(onboardingActionPolicy, onboardingDocumentService, $state, $scope, $uibModal, $q, utilityService, localeService, onboardingDocumentViewService, employeeService) {
    'ngInject';
    const vm = this;
    // APEX-977: read-only View mode (locked forms) - hides inline Edit, keeps fields disabled.
    vm.isViewOnly = () => !!vm.application && onboardingActionPolicy.getButtonTitle('2', vm.application) === 'View';
    vm.taxForm;

    vm.onPreview = onPreview;
    vm.onPreviewInPage = onPreviewInPage;
    vm.onDocDownload = onDocDownload;
    vm.onSubmit = onSubmit;
    vm.onShowI9 = onShowI9;
    vm.onClose = onClose;
    vm.onRequestReview = onRequestReview;
    vm.onSave = onSave;
    vm.revealSSNChar = revealSSNChar;
    vm.formatSsn = formatSsn;
    vm.retrieveLocale = retrieveLocale;
    vm.retrieveLocaleTranslations = retrieveLocaleTranslations;
    vm.syncFormToProfile = syncFormToProfile;
    vm.stateWitholdingTypeID = 5;
    vm.reqReview = false;
    vm.isSigned = false;
    vm.prefilled = false;
    vm.editable = true;
    vm.showForm = true; // employees must pick IL residency before the form is revealed
    vm.disableEmpFields = true;
    vm.disableEmprFields = false;
    vm.isSaved = false;
    vm.savedData = false;
    vm.showSavedNotification = false;
    vm.previewUrl;
    vm.fillLockedInfo = fillLockedInfo;
    vm.onEditClicked = onEditClicked;
    vm.state = { view: 'W4' };

    vm.$onInit = function () {
        if (vm.resolve) {
            Object.assign(vm, vm.resolve);
        }
        vm.reqReview = false;
        vm.date = moment(new Date(), 'MM/DD/YYYY').toDate();
        vm.stateProvinces = this.provinces;
        vm.userProfile = this.profile;

        vm.retrieveLocale()
            .then(function (locale) {
                return vm.retrieveLocaleTranslations(locale.localeID);
            })
            .then(function (translations) {
                vm.employeeBankInfo.Translations = translations;
            });

        if (vm.savedInputs != null && vm.savedInputs.FullName != null && !vm.ownerView) {
            vm.savedData = true;
            vm.showSavedNotification = true;
            fillEmployee(vm.savedInputs);
        } else if (vm.userInputs != null && vm.userInputs.FullName != null) {
            if (this.application) {
                vm.editable = !(this.application && this.application.HasSW4Form !== null && this.application.ProvinceW4 !== 'Review' && this.application.ProvinceW4 !== 'Submitted' && this.application.ProvinceW4 !== 'Pending');
            }
            fillEmployee(vm.userInputs);
            if (vm.application.ProvinceW4 === "Pending") {
                fillLockedInfo();
            }
        } else {
            prefillFromProfile.call(this);
        }

        if (vm.application.ApplicationStatus === 'Approved' || vm.application.ApplicationStatus === 'Submitted') {
            vm.editable = false;
        }
        vm.editable = resolveEditable();
        // APEX-321 owner side: an exempt/nonresident opt-out (addlInputs flag set, no submitted IL-W-4
        // data) confirms receipt of the employee's paperwork, then approves. Otherwise regular flow.
        if (vm.ownerView) {
            vm.showForm = false;
            // Exempt opt-out: show the receipt-confirmation + Approve popup only when the owner
            // clicks Review (showSignaturePopup). A plain open shows nothing until Review.
            if (isExemptOptOut()) {
                if (vm.showSignaturePopup) {
                    return promptOwnerExemptApprove();
                }
                return;
            }
            vm.showForm = true;
            if (vm.showSignaturePopup) {
                return onPreview();
            }
            return;
        }
        // "View" state - the application itself is Submitted (pending owner review): read-only, no
        // submission. Matches the dashboard button (getButtonTitle shows "View" only for
        // ApplicationStatus === 'Submitted', "Update"/"Fill" otherwise).
        //  - recorded resident: reveal the form in its disabled state (no popup; Review & Sign is
        //    hidden for Submitted, so nothing can be re-submitted).
        //  - recorded nonresident: show the recorded choice in a read-only residency popup.
        if (vm.application.ApplicationStatus === 'Submitted') {
            if (initialResidencyChoice() === 'resident') {
                vm.showForm = true;
                return;
            }
            return promptResidency(true);
        }
        // Fill / Update / reselect (app Pending or Approved): interactive residency gate so the
        // choice can be reselected. Resident re-fills the IL-W-4, nonresident re-records the opt-out;
        // either way the form returns to Submitted for re-approval.
        vm.showForm = false;
        promptResidency(false);
    }

    function resolveEditable() {
        const appStatus = vm.application.ApplicationStatus;
        const formStatus = vm.application.ProvinceW4;
        if (formStatus === 'ReviewRequired') return true;
        if (formStatus === 'Approved') return false;
        // "View" state - the application is Submitted (pending owner review): locked, no submission.
        if (appStatus === 'Submitted') return false;
        // Form Submitted with the app still Pending or already Approved: Update/reselect flow. The
        // nonresident/exempt opt-out (case 2) stays editable so it can be re-recorded (no Edit
        // button); the resident case (case 1) stays locked behind its Edit button.
        if (formStatus === 'Submitted') return initialResidencyChoice() === 'nonresident';
        if (appStatus === 'Approved') return false;
        if (appStatus === 'Pending') {
            return formStatus === 'Pending' || vm.application.EmployeeStatus !== 'Pending';
        }
        return !vm.application.HasSW4Form || formStatus === 'Pending' || formStatus === 'ReviewRequired';
    }

    // APEX-321 residency selection popup. Resident -> fill IL-W-4. Nonresident/military spouse ->
    // inform the employee to provide IL-W-5-NR documentation to the employer outside Apex, then close.
    // An exempt/nonresident opt-out = the StateW4Exempt flag is 'true'. The flag alone is authoritative
    // (resident submit clears it to '', the on-form exempt checkbox never sets it), so editing an already-
    // submitted resident form and opting out is recognized the same as a fresh opt-out - stale form data
    // (userInputs.FullName) no longer masks it.
    function isExemptOptOut() {
        return !!(vm.addlInputs && vm.addlInputs[IL_EXEMPT_FIELD] === 'true');
    }

    // Step 1: residency choice. Resident -> reveal IL-W-4; nonresident/military spouse -> exempt confirm.
    function promptResidency(readOnly) {
        return $uibModal.open({
            ...FormChoiceModal,
            resolve: {
                title: () => 'Illinois Income Tax Withholding',
                prompt: () => readOnly ? 'Your recorded selection:' : 'Please select the option that applies to you:',
                options: () => ([
                    { value: 'resident', label: 'I am a resident of Illinois. Use IL-W-4.' },
                    { value: 'nonresident', label: 'I am a resident of Iowa, Kentucky, Michigan, or Wisconsin, or a military spouse. Do not withhold IL Income Taxes. I will be providing the necessary documentation to the Employer outside of Apex.' }
                ]),
                defaultValue: () => initialResidencyChoice(),
                readOnly: () => !!readOnly
            }
        }).result.then(function (choice) {
            if (choice === 'resident') {
                // Switching to resident from a recorded opt-out: its IL-W-4 data was cleared and
                // ProvinceW4 is 'Submitted' from the exempt record, so the form would open locked.
                // Unlock it so the employee can fill a fresh resident IL-W-4 - in any state, since
                // re-recording sends the form back to Submitted for re-approval. (A recorded resident
                // reselecting resident uses the Edit button instead.)
                if (vm.addlInputs && vm.addlInputs[IL_EXEMPT_FIELD] === 'true') {
                    vm.editable = true;
                }
                vm.showForm = true; return;
            }
            // Nonresident / military-spouse opt-out: always confirm documentation before recording.
            // Re-selecting option 2 re-shows the original confirmation (not a read-only notice).
            return promptExemptConfirm();
        }).catch(function () {
            return vm.close({ $value: 'ok' });
        }); // x/dismiss -> close the IL form
    }

    // The employee's previously recorded IL choice, used to pre-select the residency popup on
    // reopen (Update/View): nonresident/exempt when the opt-out flag is set, otherwise resident
    // (also the default for a fresh form).
    function initialResidencyChoice() {
        return (vm.addlInputs && vm.addlInputs[IL_EXEMPT_FIELD] === 'true') ? 'nonresident' : 'resident';
    }

    // Step 2 (employee, nonresident): required 'I confirm' before recording the exempt opt-out.
    function promptExemptConfirm() {
        return $uibModal.open({
            ...FormChoiceModal,
            backdrop: true,
            keyboard: true,
            resolve: {
                title: () => 'Confirm Documentation',
                warning: () => 'Please confirm that you have completed and provided all appropriate documentation to your employer.',
                options: () => ([{ value: 'confirm', label: 'I confirm' }]),
                requireValue: () => 'confirm',
                useCheckbox: () => true,
                requiredMessage: () => 'You must check the box to confirm before submitting.',
                defaultValue: () => null,
                submitLabel: () => 'Submit',
                showCancel: () => true,
                cancelLabel: () => 'Back'
            }
        }).result.then(function () {
            return doExemptSubmit();
        }).catch(function () {
            return promptResidency(false); // dismissed -> back to the interactive residency choice (no dead end)
        });
    }

    // Inverse of syncFormToProfile: an IL nonresident/exempt opt-out clears every profile field an
    // IL-W-4 would have written (New-Hires AA/AC/AE/AF) - the allowances, the state additional-
    // withholding row (type 5 'S'), and the state-exempt flag - so no stale IL-W-4 values remain.
    function clearProfileFromForm() {
        vm.employeeBankInfo.RegularWithholdingAllowances = null;
        vm.employeeBankInfo.AdditionalDependentAllowances = null;
        vm.employeeBankInfo.EmployeeWithholdingInfos = vm.employeeBankInfo.EmployeeWithholdingInfos.filter(function (x) {
            return x.WithholdingTypeID !== vm.stateWitholdingTypeID;
        });
        vm.employeeBankInfo.StateTaxExempt = false;
    }

    // Records the opt-out in AdditionalFields (addlInputs) + ProvinceW4 -> Submitted (no PDF), then continue.
    function doExemptSubmit() {
        // Nonresident/exempt opt-out: clear any IL-W-4 values previously written to the profile,
        // then record the opt-out (addlInputs) and mark ProvinceW4 Submitted (no PDF).
        clearProfileFromForm();
        const trans = $q.resolve()
            .then(function () {
                return employeeService.updateEmployeeOnboardingBankInfo(vm.employee.EmployeeID, vm.employeeBankInfo);
            })
            .then(function () {
                return onboardingDocumentService.setAddlInput(vm.employee.EmployeeID, IL_EXEMPT_FIELD, 'true');
            })
            .then(function () {
                return onboardingDocumentService.markProvinceW4Exempt(vm.employee.EmployeeID);
            })
            .then(function () {
                return onShowI9();
            });
        utilityService.showLoadingUntilResolved(utilityService.alertOnError(trans, 'Submit failed'));
        return trans;
    }

    // Owner side (exempt opt-out): confirm receipt (Yes required) then approve.
    function promptOwnerExemptApprove() {
        return $uibModal.open({
            ...FormChoiceModal,
            backdrop: true,
            keyboard: true,
            resolve: {
                title: () => 'Confirm Receipt',
                warning: () => 'Please confirm that you have received all appropriate forms from your employee. Reminder that tax forms need to be kept for 7 years. Do ensure that it is stored in a safe place.',
                options: () => ([{ value: 'yes', label: 'I confirm' }]),
                useCheckbox: () => true,
                requiredMessage: () => 'You must check the box to confirm before approving.',
                requireValue: () => 'yes',
                defaultValue: () => null,
                submitLabel: () => 'Approve',
                showCancel: () => true,
                cancelLabel: () => 'Close'
            }
        }).result.then(function () {
            return onApprove();
        }).catch(function () {
            return vm.close({ $value: 'ok' }); // owner closed without approving
        });
    }

    // Locked, profile-sourced fields shown read-only.
    function fillLockedFromProfile() {
        vm.fullName = [vm.employeeProfile.FirstName, vm.employeeProfile.MiddleName, vm.employeeProfile.LastName].filter(Boolean).join(' ');
        vm.employeeSsn = vm.employeeBankInfo.CurrentSSN;
        const a = vm.profile.HomeAddress;
        vm.address = [a.StreetLine1, a.StreetLine2, a.AptNum].filter(Boolean).join(', ');
        vm.city = a.City;
        vm.stateProvince = vm.stateProvinces.find(x => x.StateProvinceID === a?.StateProvinceID);
        vm.zipCode = a.PostalCode;
    }

    function prefillFromProfile() {
        fillLockedFromProfile();
    }
    function fillLockedInfo() {
        fillLockedFromProfile();
    }

    function onEditClicked() {
        vm.editable = !vm.editable;
        if (vm.employeeProfile != null && vm.employeeProfile.EmployeeStatusID === 1 && vm.application.ApplicationStatus === 'Pending') {
            fillLockedFromProfile();
        }
    }

    function toNumOrNull(v) {
        return v != null && v !== '' ? Number(v) : null;
    }

    function fillEmployee(data) {
        fillLockedFromProfile();
        vm.basicAllowances = toNumOrNull(data.BasicAllowances);
        vm.additionalAllowances = toNumOrNull(data.AdditionalAllowances);
        vm.addlAmount = toNumOrNull(data.AdditionalStateWithholding);
        vm.exempt = data.Exempt === 'X';
        if (data.Signature) {
            vm.signimage = data.Signature; vm.isSigned = true;
        }
        vm.prefilled = true;
        vm.date = moment(data.Date, 'MM/DD/YYYY').toDate();
    }

    // FormRequest is all-string; stringify every value. Numeric allowance lines autofill "0"
    // when blank (per APEX-321). The template "exempt" text field receives "X" when checked.
    function buildFormData(includeSignature) {
        const s = (v) => (v == null ? '' : String(v));
        const num0 = (v) => (v != null && v !== '' ? String(v) : '0'); // autofill 0 when blank
        const formData = {
            RequestTypeId: '2',
            IsI9EmployeeMode: false,
            FullName: s(vm.fullName),
            EmployeeSocialSecurityNumber: s(vm.employeeSsn),
            EmployeeAddress: s(vm.address),
            EmployeeCity: s(vm.city),
            EmployeeState: s(vm.stateProvince ? vm.stateProvince.Abbr : ''),
            EmployeeZip: s(vm.zipCode),
            BasicAllowances: num0(vm.basicAllowances),
            AdditionalAllowances: num0(vm.additionalAllowances),
            AdditionalStateWithholding: num0(vm.addlAmount),
            Exempt: vm.exempt ? 'X' : '',
            Date: moment(vm.date, 'MM/DD/YYYY').format('MM/DD/YYYY'),
            Signature: includeSignature ? vm.signimage : undefined
        };
        return formData;
    }

    $scope.$watch('vm.taxForm.$dirty', function (value) {
        if (value) {
            vm.prefilled = false;
        }
    }, false);

    function onRequestReview() {
        const trans = onboardingDocumentService.saveComment({
            ApplicationId: vm.application.ApplicationId,
            UserName: vm.owner.UserName,
            Text: vm.commentText,
        }).then((res) => {
            if (res) return onboardingDocumentService.setReviewRequired(vm.employee.EmployeeID, '2');
        }).then(() => {
            vm.close({ $value: 'ok' });
        }).catch((err) => {
            return $q.reject(err);
        });
        utilityService.showLoadingUntilResolved(utilityService.alertOnError(trans, 'Request review failed'));
    }

    function onDocDownload(pages) {
        utilityService.showLoading();
        return onboardingDocumentService.retrieveTemplate(vm.employee.UserName, '1', String(IL_STATE_ID), pages).then(function (result) {
            utilityService.hideLoading();
            return result;
        }).then(function () {
            utilityService.hideLoading();
        });
    }

    // New-Hires-File columns: AA <- Line 1 basic allowances, AC <- Line 2 additional allowances,
    // AE <- Line 3 additional withholding (type 5 'S'), AF <- exempt checkbox. Z/AB/AD not on the IL-W-4.
    function syncFormToProfile() {
        vm.employeeBankInfo.RegularWithholdingAllowances = vm.basicAllowances;
        vm.employeeBankInfo.AdditionalDependentAllowances = vm.additionalAllowances;
        vm.employeeBankInfo.EmployeeWithholdingInfos = vm.employeeBankInfo.EmployeeWithholdingInfos.filter(function (x) {
            return x.WithholdingTypeID !== vm.stateWitholdingTypeID;
        });
        if (vm.addlAmount != null && vm.addlAmount !== '') {
            vm.employeeBankInfo.EmployeeWithholdingInfos.push({
                EmployeeId: vm.employee.EmployeeID,
                WithholdingTypeID: vm.stateWitholdingTypeID,
                AdditionalWithholdingAmount: vm.addlAmount
            });
        }
        vm.employeeBankInfo.StateTaxExempt = !!vm.exempt;
    }

    function onPreviewInPage() {
        if (vm.taxForm && vm.taxForm.$invalid) {
            alert("Form is invalid. Fill the required fields."); return;
        }
        utilityService.showLoading();
        const formData = buildFormData(false);
        return onboardingDocumentService.previewUrl(vm.employee.UserName, formData, IL_STATE_ID)
            .then(function (result) {
                utilityService.hideLoading();
                vm.previewUrl = result;
                return $uibModal.open({
                    ...PreviewSignatureModal,
                    resolve: { title: () => "Illinois IL-W-4", previewUrl: () => vm.previewUrl }
                }).result;
            }).then(function (data) {
                if (!data || !data.dataUrl) {
                    utilityService.hideLoading(); return;
                }
                vm.signimage = data.dataUrl;
                vm.isSigned = data.dataUrl != null;
                return onSubmit();
            }).catch((err) => {
                console.log(err); utilityService.hideLoading();
            });
    }

    function onSave() {
        if (!vm.editable) return; // read-only/View state (e.g. app Submitted): never save
        utilityService.showLoading();
        const formData = buildFormData(false);
        return onboardingDocumentService.save(vm.employee.UserName, formData, IL_STATE_ID).then(function () {
            vm.isSaved = true;
            vm.showSavedNotification = true;
            vm.taxForm.$setPristine();
            return utilityService.hideLoading();
        }).catch(function (err) {
            console.log(err); return utilityService.hideLoading();
        });
    }

    function onApprove() {
        const approvePromise = onboardingDocumentService.approveOnboardingForm(vm.application.ApplicationId, "2");
        const trans = $q.all(approvePromise).then((res) => {
            vm.onShowI9();
        }).catch((err) => {
            return $q.reject(err);
        });
        utilityService.showLoadingUntilResolved(utilityService.alertOnError(trans, "Approve failed"));
    }

    function onSubmit() {
        if (vm.taxForm && vm.taxForm.$invalid) {
            alert("Form is invalid. Fill the required fields."); return;
        }
        const updatePromises = [];
        vm.isDirtyEdit = vm.employeeProfile != null && vm.employeeProfile.EmployeeStatusID !== 2 && vm.application.ApplicationStatus === 'Pending';
        const isPendingResubmit = vm.application.ApplicationStatus === 'Pending' && (vm.application.ProvinceW4 === 'Approved' || vm.application.ProvinceW4 === 'Pending');
        if ((vm.taxForm && vm.taxForm.$dirty) || vm.savedData || vm.isSaved || vm.ownerView || vm.isDirtyEdit || isPendingResubmit || vm.isSigned || vm.application.ProvinceW4 === 'ReviewRequired') {
            if (!vm.isSigned) {
                utilityService.hideLoading(); alert("Preview and Signature are required");
            }
            if (!vm.signimage) {
                utilityService.hideLoading(); alert("Signature is required"); return;
            }
            const formData = buildFormData(true);
            if (!vm.ownerView) {
                syncFormToProfile();
                updatePromises.push(() => employeeService.updateEmployeeOnboardingBankInfo(vm.employee.EmployeeID, vm.employeeBankInfo));
                // Clear a stale nonresident/exempt opt-out flag when a real (resident) IL-W-4 is submitted.
                if (vm.addlInputs && vm.addlInputs[IL_EXEMPT_FIELD] === 'true') {
                    updatePromises.push(() => onboardingDocumentService.setAddlInput(vm.employee.EmployeeID, IL_EXEMPT_FIELD, '')); // clear (empty = no exempt/withhold choice)
                }
            }
            updatePromises.push(() => onboardingDocumentService.submit(vm.employee.UserName, formData, IL_STATE_ID));
        }
        if (!Array.isArray(updatePromises) || updatePromises.length === 0) {
            if (vm.ownerView) {
                return onApprove();
            } else {
                return onShowI9();
            }
        }
        const trans = updatePromises.reduce((chain, promise) => {
            return chain.then(() => (typeof promise === 'function' ? promise() : promise));
        }, $q.resolve()).then((res) => {
            if (vm.ownerView) {
                return onApprove();
            } else {
                return onShowI9();
            }
        }).catch((err) => {
            return $q.reject(err);
        });
        utilityService.showLoadingUntilResolved(utilityService.alertOnError(trans, "Save failed"));
    }

    function onPreview() {
        if (vm.taxForm && vm.taxForm.$invalid) {
            alert("Form is invalid. Fill the required fields."); return;
        }
        utilityService.showLoading();
        const formData = buildFormData(true);
        return onboardingDocumentService.previewUrl(vm.employee.UserName, formData, IL_STATE_ID)
            .then(function (result) {
                utilityService.hideLoading();
                vm.previewUrl = result;
                return $uibModal.open({
                    ...OwnerPreviewSignatureModal,
                    resolve: {
                        title: () => "Illinois IL-W-4",
                        previewUrl: () => vm.previewUrl,
                        allowApprove: () => vm.application.ApplicationStatus === 'Approved' || (canApproveForm(vm.application, '2')),
                        allowSign: () => false,
                        showSignaturePopup: () => vm.showSignaturePopup
                    }
                }).result;
            }).then(function (data) {
                if (data === 'terminate' && vm.showSignaturePopup) {
                    return vm.close({ $value: 'ok' });
                }
                if (data && data.success) {
                    vm.employerSignimage = data.dataUrl; vm.isSigned = true; return onSubmit();
                }
            }).catch((err) => {
                console.log(err); utilityService.hideLoading();
            });
    }

    $scope.$on('modal.closing', function (event, reason, closed) {
        if (reason !== 'ok') {
            return onClose(event);
        }
    });

    function onClose(event) {
        if (vm.taxForm && vm.taxForm.$dirty) {
            event.preventDefault();
            $uibModal.open({
                ...InfoModal,
                resolve: {
                    notification: () => "Your form is not completed yet. Do you want to leave?",
                    state: () => "application-status"
                },
                controller: function ConfirmModalController($uibModalInstance) {
                    'ngInject'
                    this.close = function () {
                        $uibModalInstance.dismiss();
                    };
                },
                size: 'sm',
                backdrop: 'static'
            }).result.then(function (data) {
                if (data === 'ok') {
                    vm.close({ $value: 'ok' });
                }
            }, function (dismiss) {
                console.log(dismiss);
            });
        }
    }

    function onShowI9() {
        vm.close({ $value: 'ok' });
    }

    function retrieveLocaleTranslations(localeID) {
        return localeService.retrieveLocaleTranslations(localeID).then(function (translations) {
            return translations;
        });
    }

    function retrieveLocale() {
        return localeService.retrieveLocales()
            .then(function (locales) {
                return locales.filter(function (locale) {
                    return locale.countryID === vm.employeeBankInfo.CountryID;
                })[0] || localeService.retrieveCurrentLocale();
            })
            .then(function (locale) {
                if (locale) {
                    return locale;
                }
            });
    }

    function formatSsn(ssn) {
        if (!ssn) return "";
        var digits = ("" + ssn).replace(/[^0-9]/g, "");
        if (digits.length !== 9) return ssn;
        var mask = vm.employeeBankInfo
            && vm.employeeBankInfo.Translations
            && vm.employeeBankInfo.Translations.SSNMask;
        if (mask === "000-000-000") {
            return digits.slice(0, 3) + "-" + digits.slice(3, 6) + "-" + digits.slice(6, 9);
        }
        return digits.slice(0, 3) + "-" + digits.slice(3, 5) + "-" + digits.slice(5, 9);
    }

    function revealSSNChar(ssn, char, i) {
        return i > 4;
    }
}

export const ILW4Modal = {
    component: "apexILW4Modal",
    windowClass: 'xx-dialog pagespinner',
    size: 'lg',
    backdrop: 'static'
}
